import angular from 'angular';
import csmUtilKeyCodes from '../keycodes/keycodes';
import { userAgent } from './UserAgent';
import { windowSize } from './WindowSize';

/*******************************************************************************
 * Module Tools
 *******************************************************************************/
export default angular
  .module('csm.util.tools', [csmUtilKeyCodes])
  .constant('UserAgent', userAgent)
  .factory('WindowSize', windowSize)

  /*****************************************************************************
   * Constant: Tools
   *
   * This is made as a constant so it can be used during the configuration phase.
   *****************************************************************************/
  .constant('Tools', {
    /***************************************************************************
     * coalesce */
    /**
     * Returns the first non-null(NaN if number) value from arguments. Otherwise null
     * is returned.
     *
     * @param    ...args   List of objects to check
     * @returns            First defined object
     ***************************************************************************/
    coalesce: function () {
      for (var i = 0; i < arguments.length; i++) {
        var value = arguments[i];

        if (
          value != null &&
          value != undefined &&
          !(typeof value == 'number' && isNaN(value))
        )
          return value;
      }

      return null;
    },
    /**
     * The purpose of this is to make it so the dev who is creating a error handler for a service call can easily get
     * the error message from the error response no matter the type of response from the server.
     */
    serviceErrorHandler: (error) => {
      const defaultMessage =
        'An unexpected error has occurred. Please try again.';

      if (error == null) {
        console.error(
          'An unexpected error has occurred. Please try again. Passed in error was null.'
        );
        return defaultMessage;
      }

      console.error(error);

      /**
       * If we got an unexpected error on the server or we used the Service Error class
       * to stop the process, those use statusText to send the error message to the client.
       *
       * Ex: ServiceError.BadCriteria("You are not allowed to do that!") -- Throws on the actual server and the base common handler knows to throw the 418.
       *
       * However in the cases where we only want to send an error to the client side without throwing an error on the server we use.
       *
       * Ex: HTTPResponse.status.error("Hey you are not setup properly!").write();
       *
       * This will be sent up with an error object that has message in it.
       *
       * ex: {
       *  data: {
       *    message: "Hey you are not setup properly!"
       *    status: "error"
       *  }
       * }
       */
      if (error.statusText) {
        return error.statusText;
      } else if (error.data?.message) {
        return error.data.message;
      } else if (error.message) {
        return error.message;
      } else {
        return defaultMessage;
      }
    },

    /*****************************************************************************
     * findArrayItem */
    /**
     * Returns the item in the provided array that contains the provided value for
     * the given property.
     *
     * @param   array       Array to search
     * @param   prop        Property to check
     * @param   value       Value to check
     * @param   defaultItem Default item to return when no match is available
     * @return              Corresponding object if available
     *****************************************************************************/
    findArrayItem: function (array, prop, value, defaultItem) {
      return (
        (array || [])
          .filter(function (item) {
            return item[prop] == value;
          })
          .shift() || defaultItem
      );
    },

    /*****************************************************************************
     * formatPhone */
    /**
     * Returns a formatted string from the digit only input string.
     *
     * @param   input  Input string to format
     * @param   output Indication to always have output. i.e.: (123)54
     * @return         Formatted input string
     *****************************************************************************/
    formatPhone: function (input, output) {
      var iString = input || '';
      if (iString.length < 10 && !output) return '';

      /*----------------------------------------------------*/
      /* This function ensures the input string will be the */
      /* specified length. Spaces will be used as padding   */
      /*----------------------------------------------------*/
      var getLengthString = function (str, length) {
        var s = str || '';
        while (s.length < length) s += ' ';

        return s;
      };

      var m = iString.match(/(\d{0,3})(\d{0,3})(\d{0,4})(\d*)/);

      return (
        '(' +
        getLengthString(m[1], 3) +
        ') ' +
        getLengthString(m[2], 3) +
        '-' +
        getLengthString(m[3], 4) +
        ((m[4] || '').length > 0 ? ' -' + m[4] : '')
      );
    },

    /***************************************************************************
     * generatePermutations */
    /**
     * Creates an array of unique combinations from the elements in the provided
     * <b>src</b> array, starting at the given <b>pos</b> postion.
     *
     * @param  src  Source array of objects
     * @param  pos  Starting position.
     * @param  list Output list.
     ***************************************************************************/
    generatePermutations: function (src, pos, list) {
      if (pos == undefined) pos = 0;
      if (list == undefined) list = [];

      /*-------------------------------------------------------*/
      /* Utility function to swap elements in the source array */
      /*-------------------------------------------------------*/
      var swap = function (pos1, pos2) {
        var o = src[pos1];
        src[pos1] = src[pos2];
        src[pos2] = o;
      };

      /*---------------------------------------*/
      /* Add the copy of the source when there */
      /* are no more items to iterate through  */
      /*---------------------------------------*/
      if (src.length - pos == 1) list.push(src.concat());
      /*----------------------------------------------------------------------*/
      /* Otherwise iterate through the elements to generate all combinations  */
      /* by swapping elements before recursively calling this method. The     */
      /* elements are swapped back afterwards to preserve the original order. */
      /*----------------------------------------------------------------------*/ else
        for (var i = pos; i < src.length; i++) {
          swap(pos, i);
          this.generatePermutations(src, pos + 1, list);
          swap(pos, i);
        }

      return list;
    },
    /*****************************************************************************
     * isEmpty */
    /**
     * Returns an indication if the input is empty indicated by being undefined,
     * null, NaN or an empty string.
     *
     * @param   input Input value to check.
     * @return        Boolean indication
     *****************************************************************************/
    isEmpty: function (input) {
      return (
        input == undefined ||
        input == null ||
        String(input).length == 0 ||
        (isNaN(input) && typeof input == 'number')
      );
    },

    /*****************************************************************************
     * isNaNZero */
    /**
     * Returns an indication if the input is NaN or 0.
     *
     * @param   input Input value to check.
     * @return        Boolean indication
     *****************************************************************************/
    isNaNZero: function (input) {
      return !!!Number(input);
    },

    /*****************************************************************************
     * mapUniqueObjects */
    /**
     * Returns an array of unique objects based on the keyProp found in the elements
     * of the provided list. The uniques objects will contain the field values from
     * the item. If the field is an object the key is the field value to grab while
     * the value will be the property name.
     *
     * @param  list    Source list of items to check
     * @param  keyProp Property name to check for uniqueness
     * @param  fields  Array of string|objects to create unique object.
     * @return         Array of unique objects
     *****************************************************************************/
    mapUniqueObjects: function (list, keyProp, fields) {
      /*----------------*/
      /* No source list */
      /*----------------*/
      if (!list) return null;

      var keyValues = [];
      var output = [];

      for (var i = 0; i < list.length; i++) {
        /*-------------------------------------------------*/
        /* When there is no keyProp, the object is the key */
        /*-------------------------------------------------*/
        var key = keyProp ? list[i][keyProp] : list[i];

        /*----------------------------------------------------------*/
        /* If the key has not been mapped, create the unique object */
        /*----------------------------------------------------------*/
        if (keyValues.indexOf(key) == -1) {
          var unique = null;

          /*--------------------------------*/
          /* If there are no fields to map, */
          /* the whole object will be used  */
          /*--------------------------------*/
          if (!fields || fields.length == 0) unique = list[i];
          else {
            unique = {};

            /*---------------------------------------------------------*/
            /* The fields can be array of string|objects. If an object */
            /* the key is the list item property to grab and the value */
            /* is the name of the property to set on the unique item   */
            /*---------------------------------------------------------*/
            for (var j = 0; j < fields.length; j++) {
              var isObject = angular.isObject(fields[j]);

              var itemProp = isObject ? Object.keys(fields[j])[0] : fields[j];
              var uniqueProp = isObject ? fields[j][itemProp] : itemProp;

              unique[uniqueProp] = list[i][itemProp];
            }
          }

          output.push(unique);
          keyValues.push(key);
        }
      }

      return output;
    },

    /*****************************************************************************
     * parseDate */
    /**
     * Returns a date object for the provided input. When the input evaluates to
     * a string object, the following formats are supported where mmmm is the
     * short/long month name:
     *
     *         (mmmm) (dd)[ , ]?(yy(yy))
     *         (dd) (mmmm)[ , ]?(yy(yy))
     *         (yyyy)[/- ](mm)[/- ](dd)
     *         (mm) (dd) (yy(yy))
     *         (mm)(dd)(yy(yy))
     *
     * @param  input    Input object to parse
     * @param  century  Century to use when 2-digit years are parsed. Default=2000
     * @return          Date object from the input. Null=Invalid date input.
     *****************************************************************************/
    parseDate: function (input, century) {
      /** Mapping of regex search object.  Each entry is used to search a string
    for a valid date.  re = regex, map = group to MDY mapping, alpha = true 
    if month input is alphabetical.
    @private 
    **/
      var reDateMap = [
        /* (mmmm) (dd)[ , ]?(yy(yy)) */ {
          re: /^\s*([A-Za-z]+)\s*(\d+)\s*(?:,?\s*(\d{2}|\d{4})\s*)$/i,
          map: 'mdy',
          alpha: true
        },
        /* (dd) (mmmm)[ , ]?(yy(yy)) */ {
          re: /^\s*(\d+)\s*([A-Za-z]+)\s*(?:,?\s*(\d{2}|\d{4})\s*)$/i,
          map: 'dmy',
          alpha: true
        },
        /* (yyyy)[/- ](mm)[/- ](dd)  */ {
          re: /^\s*(\d{4})(?:[\/-]|\s+)(\d{1,2})(?:[\/-]|\s+)(\d{1,2})\s*$/,
          map: 'ymd',
          alpha: false
        },
        /* (mm) (dd) (yy(yy))        */ {
          re: /^\s*(\d{1,2})(?:[\/-]|\s+)(\d{1,2})(?:(?:[\/-]|\s+)(\d{2}|\d{4}))\s*$/,
          map: 'mdy',
          alpha: false
        },
        /* (mm)(dd)(yy(yy))          */ {
          re: /^\s*(\d{1,2})(\d{2})(\d{2}|\d{4})?\s*$/,
          map: 'mdy',
          alpha: false
        }
      ];

      /** Month Name Array. */
      var mMonths = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ];

      if (angular.isDate(input)) return input;

      if (century == undefined || isNaN(century)) century = 2000;

      var valueString = (input || '').toString();

      /*---------------------------------------------------------------*/
      /* Search regex objects for an input match. Case is insensitive. */
      /*---------------------------------------------------------------*/
      for (var i = 0; i < reDateMap.length; i++) {
        var reo = reDateMap[i];
        var ma = valueString.match(reo.re);

        if (!ma) continue;

        /*--------------------------------------------*/
        /* Match found. Build object with date parts. */
        /*--------------------------------------------*/
        var match = {};

        for (var j = 1; j < ma.length; j++)
          match[String(reo.map).charAt(j - 1)] = ma[j];

        /*------------------------------------------*/
        /* If month is alpha, search long and short */
        /* month names and convert to month number. */
        /*------------------------------------------*/
        if (reo.alpha) {
          var mi = 0;

          mMonths.some(function (month, i) {
            if (
              [month.toLowerCase(), month.toLowerCase().substr(0, 3)].indexOf(
                match.m.toLowerCase()
              ) > -1
            ) {
              mi = i + 1;
              return true;
            }
            return false;
          });

          /*------------------------------------------*/
          /* Use month if found, else try next regex. */
          /*------------------------------------------*/
          if (mi > 0) match.m = mi;
          else continue;
        }

        /*--------------------------------*/
        /* Convert all values to numbers. */
        /*--------------------------------*/
        match.m = Number(match.m);
        match.d = Number(match.d);
        match.y = Number(match.y);

        /*-----------------------------------*/
        /* Use default century if not given. */
        /*-----------------------------------*/
        if (match.y < 100) match.y += century;

        /*-----------------------------------------------------*/
        /* Validate the date. Date object will actually        */
        /* translate bad date values into good ones. 2-29-2001 */
        /* becomes 3-1-2001. Here we create the date and       */
        /* verify the values are the same as the input.        */
        /*-----------------------------------------------------*/
        var d = new Date(match.y, match.m - 1, match.d);

        if (
          d.getFullYear() != match.y ||
          d.getMonth() != match.m - 1 ||
          d.getDate() != match.d
        )
          continue;

        /*--------------------*/
        /* Return valid date. */
        /*--------------------*/
        return d;
      }

      /*------------------------------------*/
      /* No match found or date is invalid. */
      /*------------------------------------*/
      return null;
    },

    /*****************************************************************************
     * parsePhone */
    /**
     * Parses out the phone digits from the provided string.
     *
     * @param   input  Input string to parse
     * @returns        Digit string
     *****************************************************************************/
    parsePhone: function (input) {
      /** Regex Expression used to parse the phone number.
    <p>Groups</p>
      <ol>
        <li>Area Code</li>
        <li>Prefix</li>
        <li>Suffix</li>
        <li>Extension (optional)</li>
      </ol> 
    **/
      var phoneRegEx =
        /^(?:1[-\s]?)?\(?(\d{3})[\)-]?\s?(\d{3})[-\s]?(\d{4})(?:(?:\s?(?:(?:(?:ext\.?)|[x+-])\s?)?)?(\d+))?$/i;
      var s = (input || '').trim();
      var ra = s.match(phoneRegEx);

      return ra != null ? ra[1] + ra[2] + ra[3] + (ra[4] ? ra[4] : '') : null;
    },

    /*****************************************************************************
     * roundMoney */
    /**
     * Rounds the provided input to two decimal places.
     *
     * @param   n  Number to round.
     * @return     `n` rounded two decimal places.
     *****************************************************************************/
    roundMoney: function (n) {
      return this.roundToPrecision(n, 2);
    },

    formatMoney: function (n) {
      return this.formatToPrecision(n, 2);
    },
    formatToPrecision: function (n, p) {
      return isNaN(n) || isNaN(p) ? n : this.roundToPrecision(n, p).toFixed(p);
    },

    /*****************************************************************************
     * roundToPrecision */
    /**
     * Rounds the provided input to the specified precision.
     *
     * JS `Math.round()` rounds to the nearest integer. From the docs: "If the
     * fractional portion is exactly 0.5, the argument is rounded to the next
     * integer in the direction of +∞." This method rounds "Away From Zero" as we
     * do on the server side. Neither `Number.toFixed()` nor `Number.toPrecision()`
     * work as expected but this method does the job.
     *
     * The absolute value of `n` is shifted left `p` digits, rounded using
     * `Math.round()`, shifted right `p` digits, and converted back to the proper
     * sign.
     *
     * @param  n  Number to round.
     * @param  p  Decimal digit precision.
     * @return    `n` rounded to `p` decimal places.
     *****************************************************************************/
    roundToPrecision: function (n, p) {
      /*-------------------------------------------------------------------------------------------------*/
      /* TODO: Polyfill for Math.sign() which IE does not support. Can be removed when moved over to the */
      /* TODO: new build since ES 6 will be fully shimmed.                                               */
      /*                                                                                                 */
      /* See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/sign */
      /*-------------------------------------------------------------------------------------------------*/
      var sign = function (x) {
        return (x > 0) - (x < 0) || +x;
      };

      n = isNaN(n) ? 0 : n;
      p = isNaN(p) ? 0 : p;
      var e = Math.pow(10, p);
      return (sign(n) * Math.round(Math.abs(n) * e)) / e;
    },

    /*****************************************************************************
     * lastFiscalYearStart */
    /**
     * Returns the start date of the last fiscal year based on the provided 
     * current fiscal year start month and day. The
     *
     * @param  fyMonth  Fiscal year start month (1-indexed)
     * @param  fyDay    Fiscal year start day
     * @return          Date object representing the first day of the last FY
     *****************************************************************************/
    lastFiscalYearStart: function (fyMonth, fyDay) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentFundingYearFullDate = new Date(currentYear, fyMonth - 1, fyDay);
      const currentFundingYear = today >= currentFundingYearFullDate ? currentYear : currentYear - 1;
      return new Date(currentFundingYear - 1, fyMonth - 1, fyDay);
    },

    /*****************************************************************************
     * toHex */
    /**
     * Takes the provided number input and converts it to a hexadecimal string.
     *
     * @param   input      Input number to convert
     * @returns            Converted string value.
     *****************************************************************************/
    toHex: function (input) {
      var hex = (Number(input) || 0).toString(16);

      /*-----------------------*/
      /* Pad to six characters */
      /*-----------------------*/
      for (var i = hex.length; i < 6; i++) hex = '0' + hex;

      return '#' + hex;
    }
  }).name;
